<template>
  <div class="product_historical__report">
    <ReportFormat :records="reports_data" @show_result="show_result" @download_excel_report="exportExcel" :isLoading="loading">
      <!-- Form -->
      <template #report-form>
        <div class="vx-row pt-5 px-5">
          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <!-- from date -->
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>From Date</h6>
              </div>
              <div class="vx-col w-3/5 relative">
                <date-picker :appendToBody="false" v-model="fromDate" placeholder="MM-DD-YYYY" format="MM-DD-YYYY" valueType="format" class="w-full" v-validate="'required'" name="Report From Date" />
              </div>
            </div>

            <!-- item name -->
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Item Name</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  multiple
                  autocomplete
                  :closeOnSelect="false"
                  class="w-full"
                  :value.sync="item"
                  placeholder="All"
                  @input="(elem) => (item = elem.value)"
                  name="Item"
                  label="product_name"
                  val="product_name"
                  :ssr="true"
                  action="product/getProductList"
                />
              </div>
            </div>

            <!-- Category Dropdown -->
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Category</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  autocomplete
                  multiple
                  :closeOnSelect="false"
                  class="w-full category-input"
                  :value.sync="categoryId"
                  @input="(item) => (categoryId = item && item.value)"
                  name="Category"
                  placeholder="All"
                  label="category_name"
                  val="id"
                  v-validate="'required'"
                  :params="{ is_global: user.roleId === DEMORETAILER ? null : 'global', is_status: true }"
                  action="product/getAllProductCategories"
                />
                <span class="text-danger text-sm" v-show="errors.has('Category')">{{ errors.first('Category') }}</span>
              </div>
            </div>
          </div>

          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <!-- to date -->
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>To Date</h6>
              </div>
              <div class="vx-col w-3/5 relative">
                <date-picker :appendToBody="false" v-model="toDate" placeholder="MM-DD-YYYY" format="MM-DD-YYYY" valueType="format" class="w-full" v-validate="'required'" name="Report To Date" />
              </div>
            </div>

            <!-- Created by -->
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Created By</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  class="w-full"
                  autocomplete
                  :value="createdBy"
                  @input="(elem) => (createdBy = elem.value)"
                  placeholder="Please Select Created By"
                  name="Sales By"
                  :extra="[{ label: 'All', value: null }]"
                  :options="sales_user_list_options"
                  :loading="createdByLoading"
                />
              </div>
            </div>

            <!-- Transaction Type Dropdown -->
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Transaction Type</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2 class="w-full" autocomplete :value="transaction_type" @input="(elem) => (transaction_type = elem.value)" placeholder="All" name="Sales By" :options="transactionType" />

                <span class="text-danger text-sm" v-show="errors.has('transactonTytpe')">{{ errors.first('transactonTytpe') }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #record-table>
        <template>
          <vs-th> # </vs-th>
          <vs-td>Invoice # </vs-td>
          <vs-td> Transaction Type</vs-td>
          <vs-td> Category </vs-td>
          <vs-td> Item Name</vs-td>
          <vs-td> Change in Quantity </vs-td>
          <vs-td> Weight Type</vs-td>
          <vs-td> Created By</vs-td>
          <vs-td>Adjustment Date</vs-td>
          <vs-td>Date Adjustment Posted</vs-td>
          <vs-td> Action</vs-td>
        </template>

        <template ref="tableBody">
          <vs-tr :data="tr" :key="i" v-for="(tr, i) in reports_data">
            <vs-td>{{ i + 1 }}</vs-td>
            <vs-td>{{ (tr.sales && tr.sales.sales_code) || '-' }}</vs-td>
            <vs-td>{{ tr.transactionType }} </vs-td>
            <vs-td>{{ tr.category && tr.category.category_name }} </vs-td>
            <vs-td>{{ tr.product && tr.product.product_name }} </vs-td>
            <vs-td>
              {{ [categories.FLOWER, categories.SHAKE].includes(tr.categoryId) ? convertAvailableQty(tr.changesInQuantity, 'pounds')[tr.product.weight_type] : tr.changesInQuantity | weightFilter }}
            </vs-td>
            <vs-td>{{ tr.weight_type }} </vs-td>
            <vs-td>{{ tr.user && tr.user.business_name ? tr.user.business_name : tr.user.username }} </vs-td>
            <vs-td>{{ formatDateByTimeZoneOffSet(tr.date, 'MM-DD-YYYY ', '-06:00') }}</vs-td>

            <vs-td>{{ formatDateByTimeZoneOffSet(tr.created_at, 'MM-DD-YYYY hh:mm:ss A', '-06:00') }}</vs-td>

            <vs-td>
              <feather-icon v-if="tr.notes !== null || tr.reason !== null" class="cursor-pointer p-2" icon="EyeIcon" @click="viewProductHistoricalDetailModal(tr)" />
              <span v-else>-</span>
            </vs-td>
          </vs-tr>
        </template>
      </template>
    </ReportFormat>

    <!-- pdf container -->
    <div id="pdf-container"></div>

    <!-- view product historical details -->
    <view-product-historical-details
      v-if="isViewProductHistoricalDetailsModalMounted"
      :showModal.sync="isViewProductHistoricalDetailsModalShow"
      :viewProducthistoricalDetail="viewProducthistoricalDetail"
    />
  </div>
</template>


<script>
import moment from 'moment'
import XLSX from 'xlsx'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import { dateMixins } from '@/mixins/dateMixins.js'
import ReportFormat from '@/views/reports/components/ReportFormat.vue'
import { mapState, mapMutations, mapActions } from 'vuex'
import { weightMixins } from '@/utils/weightType'
import Select2 from '@/components/custom/form-elements/Select2.vue'
import { DEMORETAILER } from '@/utils/roles.js'
import { transactionType } from '@/utils/enums'
import ViewProductHistoricalDetails from '@/views/reports/components/ViewProductHistoricalDetails.vue'

export default {
  name: 'ProductHistoricalReport',

  components: {
    DatePicker,
    ReportFormat,
    Select2,
    ViewProductHistoricalDetails
  },

  data() {
    return {
      page: 1,
      limit: 0,
      search: '',
      date: 'MM-dd-yyyy',
      fromDate: moment().format('MM-DD-YYYY'),
      toDate: moment().format('MM-DD-YYYY'),
      createdBy: null,
      transaction_type: null,
      item: [],
      loading: false,
      categoryId: [],
      filters: {},
      DEMORETAILER,
      transactionType,

      productLoading: false,
      categoryLoading: false,
      createdByLoading: false,
      // view product historical details modal
      viewProducthistoricalDetail: {
        reason: '',
        notes: ''
      },
      isViewProductHistoricalDetailsModalShow: false,
      isViewProductHistoricalDetailsModalMounted: false
    }
  },

  mixins: [weightMixins, dateMixins],

  async mounted() {
    // fetch sales user list
    await this.getSalesUserList()
  },

  computed: {
    ...mapState('pos', ['sale']),
    ...mapState('product', ['categories']),
    ...mapState('auth', ['user']),
    ...mapState('report', ['reports_data', 'sales_user_list', 'total', 'FilteredCount']),

    sales_user_list_options() {
      return this.sales_user_list.reduce((acc, item) => {
        item.label = item.username
        item.value = item.id

        acc.push(item)

        return acc
      }, [])
    }
  },

  methods: {
    moment,
    ...mapMutations('report', { set_state: 'SET_STATE' }),
    ...mapActions('pos', ['getSaleById']),
    ...mapMutations('product', { set_products_state: 'SET_STATE' }),

    handleDynamicFilter(value, key) {
      this.$set(this.filters, key, value)
    },

    // fetch report data
    async show_result() {
      this.loading = true
      const payload = {
        page: this.page,
        limit: this.limit,
        search: this.search,
        from_date: moment(this.fromDate).format('YYYY-MM-DD') ? moment(this.fromDate).format('YYYY-MM-DD') : null,
        to_date: moment(this.toDate).format('YYYY-MM-DD') ? moment(this.toDate).format('YYYY-MM-DD') : null,
        item_names: this.item || null,
        createdBy: this.createdBy || null,
        transaction_type: this.transaction_type || null,
        categoryIds: this.categoryId || null
      }

      this.$store
        .dispatch('report/geProductHistoricalReport', payload)
        .then(() => {
          this.loading = false
        })
        .catch((err) => {
          const { message } = err
          this.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    // get sales user list
    async getSalesUserList() {
      this.createdByLoading = true
      this.$store
        .dispatch('report/getSalesUserList')
        .then(() => {
          this.createdByLoading = false
        })
        .catch((err) => {
          const { message } = err
          this.createdByLoading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    async exportExcel() {
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(
        this.reports_data.map((item, index) => ({
          '#': index + 1,
          'Invoice #': item.sales && item.sales.sales_code,
          'Transaction Type': item.transactionType,
          Category: item.category && item.category.category_name,
          'Item Name': item.product && item.product.product_name,
          'Change in Quantity': weightMixins.methods.mathRound(
            [this.categories.FLOWER, this.categories.SHAKE].includes(item.categoryId)
              ? weightMixins.methods.convertAvailableQty(item.changesInQuantity, 'pounds')[item.product.weight_type]
              : item.changesInQuantity,
            3
          ),
          'Weight Type': item.weight_type,
          'Created by': item.user.business_name ? item.user.business_name : item.user.username,
          'Adjustment Date': dateMixins.methods.formatDateByTimeZoneOffSet(item.date, 'MM-DD-YYYY', '-06:00'),
          'Date Adjustment Posted': dateMixins.methods.formatDateByTimeZoneOffSet(item.created_at, 'MM-DD-YYYY hh:mm:ss A', '-06:00'),
          Notes: item.notes,
          Reason: item.reason
        }))
      )
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1')
      XLSX.writeFile(wb, 'Product Historical Report.xlsx')
    },

    //view produnt historical details
    viewProductHistoricalDetailModal(tr) {
      this.viewProducthistoricalDetail = {
        reason: tr.reason,
        notes: tr.notes
      }
      this.isViewProductHistoricalDetailsModalShow = true
      this.isViewProductHistoricalDetailsModalMounted = true
    }
  },

  watch: {
    // view produnt historical details modal
    isViewProductHistoricalDetailsModalShow: {
      handler(newVal) {
        if (!newVal) {
          setTimeout(() => {
            this.isViewProductHistoricalDetailsModalMounted = false
            this.selectedProductHistoricalId = null
          }, 0)
        }
      }
    }
  },

  async beforeDestroy() {
    await this.set_state({ action: 'reports_data', data: [] })
    await this.set_products_state({ action: 'products', data: [] })
  },

  filters: {
    //weightFilter rounded conversation
    weightFilter(value) {
      return weightMixins.methods.mathRound(value || 0, 3)
    }
  }
}
</script>
